/**
 * Curated My Facility "Add by type" options and empty-state helpers.
 * Slugs are the compliance keys; UI shows display_name only.
 */

export interface AddFacilityByTypeOption {
  slug: string;
  display_name: string;
  description: string;
}

export const CURATED_ADD_FACILITY_BY_TYPE_OPTIONS: readonly AddFacilityByTypeOption[] =
  [
    {
      slug: "adult_family_home",
      display_name: "Adult Family Home",
      description:
        "Licensed adult family home. Uses the shared residential / assisted living pack today.",
    },
    {
      slug: "assisted_living_facility",
      display_name: "Assisted Living Facility",
      description: "Licensed assisted living facility.",
    },
    {
      slug: "memory_care",
      display_name: "Memory Care",
      description:
        "Licensed dementia units or facilities only — not an Adult Family Home dementia specialty.",
    },
    {
      slug: "independent_living",
      display_name: "Independent Living",
      description: "Independent living or retirement community (not licensed as ALF).",
    },
    {
      slug: "nursing_home",
      display_name: "Nursing Home / Skilled Nursing",
      description: "Nursing home or skilled nursing facility.",
    },
    {
      slug: "home_health",
      display_name: "Home Health",
      description: "Home health agency.",
    },
    {
      slug: "hospice",
      display_name: "Hospice",
      description: "Hospice agency or hospice facility.",
    },
  ];

const EXCLUDED_MANUAL_SLUGS = new Set([
  "hospital",
  "general_acute_care_hospital",
  "acute_care_hospital",
  "critical_access_hospital",
  "cah",
  "rural_health_clinic",
  "rhc",
  "federally_qualified_health_center",
  "fqhc",
  "ambulatory_surgical_center",
  "asc",
  "dialysis",
  "end_stage_renal_disease",
  "esrd",
  "independent_living_facilities",
]);

export function getCuratedAddFacilityByTypeOptions(): AddFacilityByTypeOption[] {
  return CURATED_ADD_FACILITY_BY_TYPE_OPTIONS.filter(
    (opt) => !EXCLUDED_MANUAL_SLUGS.has(opt.slug),
  );
}

export function findCuratedAddFacilityByTypeOption(
  slug: string,
): AddFacilityByTypeOption | undefined {
  return getCuratedAddFacilityByTypeOptions().find((opt) => opt.slug === slug);
}

export interface CatalogTypeRow {
  id?: number;
  type_slug?: string;
  display_name?: string;
  /** Rules facility_type.alias_slugs when the catalog includes them. */
  alias_slugs?: string[] | null;
}

/** Curated slugs that are aliases on another catalog row (QA often has no AFH id). */
const CATALOG_SLUG_FALLBACKS: Record<string, string> = {
  adult_family_home: "assisted_living_facility",
};

function catalogSlugKey(value: unknown): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
}

function catalogRowMatchesSlug(row: CatalogTypeRow, slug: string): boolean {
  const want = catalogSlugKey(slug);
  if (!want) return false;
  if (catalogSlugKey(row.type_slug) === want) return true;
  const aliases = row.alias_slugs;
  if (!Array.isArray(aliases)) return false;
  return aliases.some((alias) => catalogSlugKey(alias) === want);
}

export interface ResolvedAddFacilityByType {
  slug: string;
  display_name: string;
  description: string;
  facility_type_id?: number;
}

/** Match curated slug to facility-types catalog id. Keep curated display name. */
export function resolveAddFacilityByTypeFromCatalog(
  slug: string,
  catalog: CatalogTypeRow[] | null | undefined,
): ResolvedAddFacilityByType | null {
  const curated = findCuratedAddFacilityByTypeOption(slug);
  if (!curated) return null;
  const fallbackSlug = CATALOG_SLUG_FALLBACKS[curated.slug];
  const match = (catalog || []).find(
    (row) =>
      catalogRowMatchesSlug(row, curated.slug) ||
      (fallbackSlug ? catalogRowMatchesSlug(row, fallbackSlug) : false),
  );
  const id = match?.id;
  return {
    slug: curated.slug,
    display_name: curated.display_name,
    description: curated.description,
    facility_type_id:
      typeof id === "number" && Number.isFinite(id) ? id : undefined,
  };
}

export function facilityValidationSupportDisclaimer(
  addByTypeEnabled: boolean,
): string {
  return addByTypeEnabled
    ? "NPI Type 2 (organizational) facilities are verified automatically. Facilities without an NPI can be added by type."
    : "Only Type 2 (organizational) NPI facilities are supported.";
}

export interface AddFacilityByTypeEmptyStateInput {
  enabled: boolean;
  isValidated: boolean;
  errorTitle?: string | null;
  error?: string | null;
  npiError?: string | null;
  displayedCount?: number;
  rawType2CandidateCount?: number;
  suggestionDisplayedCount?: number;
  suggestionRawCount?: number;
}

const MISS_TITLES = [
  "no matching facilities",
  "provider type not supported",
  "facility type not supported",
];

function textBlob(...parts: Array<string | null | undefined>): string {
  return parts
    .filter((p) => typeof p === "string" && p.trim())
    .join(" ")
    .toLowerCase();
}

/** Empty-state / Type-1 miss / NPI miss — not form-validation errors. */
export function shouldShowAddFacilityByTypeEmptyState(
  input: AddFacilityByTypeEmptyStateInput,
): boolean {
  if (!input.enabled) return false;

  const title = (input.errorTitle || "").trim().toLowerCase();
  const missTitle = MISS_TITLES.some((t) => title.includes(t));
  const err = textBlob(input.error, input.npiError);
  const missMessage =
    err.includes("no facilities found") ||
    err.includes("no results include") ||
    err.includes("no suggested facilities") ||
    err.includes("no results found for npi") ||
    err.includes("npi type 1") ||
    err.includes("individual physician");

  if (!input.isValidated && (missTitle || missMessage)) {
    return true;
  }

  const displayed = input.displayedCount ?? 0;
  const raw = input.rawType2CandidateCount ?? 0;
  if (input.isValidated && displayed === 0 && raw > 0) {
    return true;
  }

  const sugDisplayed = input.suggestionDisplayedCount ?? 0;
  const sugRaw = input.suggestionRawCount ?? 0;
  if (input.isValidated && sugDisplayed === 0 && sugRaw > 0) {
    return true;
  }

  return false;
}

export interface NameAddressFacility {
  facility_name?: string | null;
  address_1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
}

function normalizeFacilityKeyPart(value: string | null | undefined): string {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export function isSameNameAndAddress(
  a: NameAddressFacility,
  b: NameAddressFacility,
): boolean {
  const nameA = normalizeFacilityKeyPart(a.facility_name);
  const nameB = normalizeFacilityKeyPart(b.facility_name);
  if (!nameA || nameA !== nameB) return false;

  const streetA = normalizeFacilityKeyPart(a.address_1);
  const streetB = normalizeFacilityKeyPart(b.address_1);
  const cityA = normalizeFacilityKeyPart(a.city);
  const cityB = normalizeFacilityKeyPart(b.city);
  const stateA = normalizeFacilityKeyPart(a.state);
  const stateB = normalizeFacilityKeyPart(b.state);
  const zipA = normalizeFacilityKeyPart(a.postal_code).slice(0, 5);
  const zipB = normalizeFacilityKeyPart(b.postal_code).slice(0, 5);

  if (streetA && streetB) {
    return streetA === streetB && cityA === cityB && stateA === stateB;
  }
  if (cityA && cityB && stateA && stateB) {
    if (cityA !== cityB || stateA !== stateB) return false;
    if (zipA && zipB) return zipA === zipB;
    return true;
  }
  return false;
}

export function findDuplicateNameAndAddress(
  candidate: NameAddressFacility,
  existing: NameAddressFacility[] | null | undefined,
): NameAddressFacility | undefined {
  return (existing || []).find((row) => isSameNameAndAddress(candidate, row));
}

export function resolveStateAbbrevFromList(
  raw: string,
  states: Array<{
    name?: string;
    state_province?: string;
    state_abbreviation?: string;
  }>,
): string {
  const t = (raw || "").trim();
  if (!t) return "";
  const upper = t.toUpperCase();
  const byAbbr = states.find(
    (s) => (s.state_abbreviation || "").toUpperCase() === upper.slice(0, 8),
  );
  if (byAbbr?.state_abbreviation) {
    return String(byAbbr.state_abbreviation).toUpperCase();
  }
  const paren = t.match(/^([A-Za-z]{2})\s*\(/);
  if (paren) return paren[1].toUpperCase();
  const tl = t.toLowerCase();
  const byName = states.find(
    (s) =>
      (s.name || "").toLowerCase() === tl ||
      String(s.state_province || "").toLowerCase() === tl,
  );
  if (byName?.state_abbreviation) {
    return String(byName.state_abbreviation).toUpperCase();
  }
  return upper.slice(0, 8);
}
